import { ArrowDownRight, ArrowUpRight, BookOpen, ChevronRight, CircleDollarSign, FileText, Landmark, ReceiptText, ShoppingCart, Sparkles, TrendingUp, Wallet } from "lucide-react";
import { money } from "@/lib/api";
import { AnalyticsBoard } from "@/components/AnalyticsBoard";

const pct = (a, b) => (b > 0 ? ((a - b) / b) * 100 : a > 0 ? 100 : 0);

export default function Dashboard({ data, k, user, onNavigate, can = () => true }) {
  if (!data) return <div className="dashboard-loading" data-testid="dashboard-loading"><div className="loading-pulse" /><p>Menyiapkan ringkasan keuangan Anda...</p></div>;
  const today = new Date();
  const hour = today.getHours();
  const salam = hour < 11 ? "Selamat pagi" : hour < 15 ? "Selamat siang" : hour < 19 ? "Selamat sore" : "Selamat malam";
  const firstName = (user?.name || "").split(" ")[0] || "Pengguna";
  const go = (v) => onNavigate && onNavigate(v);
  const monthLabel = today.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const dateLabel = today.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const trend = data.trend || [];
  const last = trend[trend.length - 1] || {};
  const prev = trend[trend.length - 2] || {};
  const revDelta = pct(last.revenue || 0, prev.revenue || 0);
  const margin = k.sales > 0 ? ((k.gross_profit / k.sales) * 100).toFixed(1) : 0;
  const profitDelta = pct((last.revenue || 0) - (last.expense || 0), (prev.revenue || 0) - (prev.expense || 0));
  const aging = data.aging || {};
  const agingRows = [
    ["Belum jatuh tempo", aging.current || 0, "teal"],
    ["1–30 hari", aging.d30 || 0, "amber"],
    ["31–60 hari", aging.d60 || 0, "orange"],
    ["> 60 hari", aging.d90 || 0, "red"],
  ];
  const agingMax = Math.max(...agingRows.map(([, v]) => v), 1);

  const kpis = [
    { label: "Total Pendapatan", value: k.sales, icon: TrendingUp, tone: "teal", meta: `${revDelta >= 0 ? "+" : ""}${revDelta.toFixed(1)}% vs bulan lalu`, up: revDelta >= 0 },
    { label: "Laba Bersih", value: k.gross_profit, icon: Wallet, tone: "green", meta: `${profitDelta >= 0 ? "+" : ""}${profitDelta.toFixed(1)}% vs bulan lalu · margin ${margin}%`, up: profitDelta >= 0 },
    { label: "Piutang Belum Dibayar", value: k.receivable, icon: FileText, tone: "amber", meta: k.overdue_count > 0 ? `${k.overdue_count} invoice jatuh tempo` : "Semua invoice lancar", warn: k.overdue_count > 0 },
    { label: "Kas & Bank", value: k.cash, icon: Landmark, tone: "blue", meta: "Saldo semua rekening", up: k.cash >= 0 },
  ];
  const actions = [
    ["Invoice Penjualan", "Buat invoice penjualan baru", ShoppingCart, "Invoice Penjualan", "penjualan"],
    ["Penerimaan Uang", "Catat pembayaran masuk", CircleDollarSign, "Penerimaan Uang", "kas"],
    ["Biaya", "Catat pengeluaran operasional", ReceiptText, "Biaya", "biaya"],
    ["Jurnal Umum", "Buat jurnal penyesuaian", BookOpen, "Jurnal Umum", "akuntansi"],
  ].filter(([, , , , m]) => can(m));
  return (
    <>
      <div className="page-heading dash-heading">
        <div>
          <p className="eyebrow">RINGKASAN BISNIS</p>
          <h1>{salam}, {firstName} <span>✦</span></h1>
          <p className="muted">Berikut kondisi keuangan bisnis Anda per hari ini.</p>
        </div>
        <div className="date-filter" data-testid="dashboard-date-filter">{dateLabel}</div>
      </div>
      <section className="dashboard-banner v2">
        <div className="banner-glow" />
        <div>
          <p className="eyebrow light">WORKSPACE AKTIF</p>
          <h2>{data.company?.name || "Perusahaan Anda"}</h2>
          <p>Periode akuntansi: {monthLabel} · IDR</p>
        </div>
        <div className="banner-meta">
          <span className="banner-status">● Semua sistem berjalan normal</span>
          {can("pengaturan") && <button data-testid="view-company-button" onClick={() => go("Pengaturan")}>Lihat detail perusahaan <ChevronRight size={15} /></button>}
        </div>
      </section>
      <div className="kpi-grid">
        {kpis.map(({ label, value, icon: Icon, tone, meta, warn, up }) => (
          <div className="kpi-card v2" data-testid={`kpi-${label.toLowerCase().replaceAll(" ", "-")}`} key={label}>
            <div className="kpi-top">
              <span className={`kpi-ic ${tone}`}><Icon size={16} /></span>
              <span className="kpi-label">{label}</span>
            </div>
            <strong>{money(value)}</strong>
            <small className={`kpi-meta ${warn ? "warn" : up ? "good" : ""}`}>{warn ? <ArrowDownRight size={12} /> : <ArrowUpRight size={12} />} {meta}</small>
          </div>
        ))}
      </div>
      <AnalyticsBoard />
      <div className="content-grid">
        {can("akuntansi") ? (
        <section className="surface transaction-card">
          <div className="section-head">
            <div><h3>Transaksi terbaru</h3><p className="muted">Jurnal terakhir yang diposting di workspace</p></div>
            <button data-testid="transaction-see-all-button" className="text-btn" onClick={() => go("Jurnal Umum")}>Lihat semua <ChevronRight size={15} /></button>
          </div>
          <table data-testid="recent-transactions-table">
            <thead><tr><th>TRANSAKSI</th><th>REFERENSI</th><th>STATUS</th><th className="right">NILAI</th></tr></thead>
            <tbody>
              {data.recent.map((x) => (
                <tr key={x.id}>
                  <td><strong>{x.description}</strong><small>{x.number}</small></td>
                  <td>{x.reference || "—"}</td>
                  <td><span className="status posted">Posted</span></td>
                  <td className="right mono">{money(x.total_debit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        ) : (
        <section className="surface action-card">
          <div className="section-head">
            <div><h3>Aksi cepat</h3><p className="muted">Mulai dari aktivitas utama</p></div>
            <Sparkles size={18} className="sparkle" />
          </div>
          {actions.map(([a, b, I, target]) => (
            <button data-testid={`quick-${a.toLowerCase().replaceAll(" ", "-")}`} className="action-row" key={a} onClick={() => go(target)}>
              <span className="action-icon"><I size={17} /></span>
              <span><strong>{a}</strong><small>{b}</small></span>
              <ChevronRight size={16} />
            </button>
          ))}
        </section>
        )}
        <section className="surface receivable-card">
          <div className="section-head">
            <div><h3>Piutang jatuh tempo</h3><p className="muted">Umur piutang berdasarkan tanggal jatuh tempo</p></div>
            {can("penjualan") && <button data-testid="receivable-detail-button" className="text-btn" onClick={() => go("Invoice Penjualan")}>Lihat semua <ChevronRight size={15} /></button>}
          </div>
          <div className="aging-total">{money(k.receivable)}<small>Total piutang belum dibayar</small></div>
          {agingRows.map(([l, v, c]) => (
            <div className="aging-row v2" key={l}>
              <div className="aging-row-top"><span><i className={`aging-dot ${c}`} />{l}</span><strong>{money(v)}</strong></div>
              <div className="aging-bar"><i className={c} style={{ width: `${(v / agingMax) * 100}%` }} /></div>
            </div>
          ))}
        </section>
      </div>
      {can("akuntansi") && (
      <div className="bottom-grid actions-only">
        <section className="surface action-card h">
          <div className="section-head">
            <div><h3>Aksi cepat</h3><p className="muted">Mulai dari aktivitas utama</p></div>
            <Sparkles size={18} className="sparkle" />
          </div>
          <div className="action-rows">
            {actions.map(([a, b, I, target]) => (
              <button data-testid={`quick-${a.toLowerCase().replaceAll(" ", "-")}`} className="action-row" key={a} onClick={() => go(target)}>
                <span className="action-icon"><I size={17} /></span>
                <span><strong>{a}</strong><small>{b}</small></span>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        </section>
      </div>
      )}
    </>
  );
}
